import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import AuthPage from "@/features/auth/pages/AuthPage";
import { AuthProvider, useAuth } from "@/features/auth/context/AuthContext";
import { ProcessRouteRedirector } from "@/shared/components/ProcessRouteRedirector";
import { AppErrorBoundary } from "@/shared/components/AppErrorBoundary";
import { useUserRole } from "@/hooks/useUserRole";
import { PwaInstallBanner } from "@/features/pwa";

// Lazy-load heavy/secondary routes so the initial Auth page ships with minimal JS,
// reducing LCP element render delay on first paint.
const Index = lazy(() => import("./pages/Index.tsx"));
const ResetPassword = lazy(() => import("@/features/auth/pages/ResetPasswordPage"));
const NotificationsPage = lazy(() => import("@/features/notifications/pages/NotificationsPage"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const BrandingTestHistory = lazy(() => import("./pages/BrandingTestHistoryPage.tsx"));
const PortalPage = lazy(() => import("./pages/PortalPage.tsx"));
const PortalTrackingPage = lazy(() => import("./pages/PortalTrackingPage.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground">
    Carregando...
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <RouteFallback />;
  if (!user) {
    // Preserva o querystring (ex.: ?process=<id>) para que /auth possa
    // restaurá-lo após o login.
    const target = `/auth${location.search}`;
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { role, loading } = useUserRole();
  if (loading) return <RouteFallback />;
  if (role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
};

const App = () => (
  <AppErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <PwaInstallBanner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <ProcessRouteRedirector>
              <Routes>
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/qa/branding-tests"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <BrandingTestHistory />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Rotas públicas — sem autenticação */}
                <Route path="/portal/:token" element={<PortalPage />} />
                <Route path="/portal/:token/rastreio/:protocolo" element={<PortalTrackingPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProcessRouteRedirector>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
